import type { RewardState } from "@/domain";

export const STAMPS_PER_COMPLETED_TASK = 1;

export interface DecorationDefinition {
  readonly id: string;
  readonly requiredStamps: number;
}

export const DECORATION_CATALOG = [
  { id: "kite-garden", requiredStamps: 3 },
  { id: "shell-lantern", requiredStamps: 6 },
  { id: "flower-arch", requiredStamps: 10 },
  { id: "harbor-windmill", requiredStamps: 15 },
  { id: "welcome-sailboat", requiredStamps: 24 }
] as const satisfies readonly DecorationDefinition[];

export interface RewardUpdate {
  readonly rewards: RewardState;
  readonly stampsAwarded: number;
  readonly newlyUnlockedDecorationIds: readonly string[];
}

export function awardCompletedTaskRewards(
  current: RewardState,
  newlyCompletedTaskCount = 1
): RewardUpdate {
  assertNonNegativeInteger(current.stamps, "Reward stamps");
  assertNonNegativeInteger(
    newlyCompletedTaskCount,
    "Newly completed task count"
  );

  if (newlyCompletedTaskCount === 0) {
    return {
      rewards: current,
      stampsAwarded: 0,
      newlyUnlockedDecorationIds: []
    };
  }

  const stampsAwarded =
    newlyCompletedTaskCount * STAMPS_PER_COMPLETED_TASK;
  const stamps = current.stamps + stampsAwarded;
  assertNonNegativeInteger(stamps, "Updated reward stamps");
  const existingDecorationIds = [...new Set(current.unlockedDecorationIds)];
  const existingDecorationIdSet = new Set(existingDecorationIds);
  const eligibleDecorationIds = DECORATION_CATALOG.filter(
    (decoration) => decoration.requiredStamps <= stamps
  ).map((decoration) => decoration.id);
  const newlyUnlockedDecorationIds = eligibleDecorationIds.filter(
    (decorationId) => !existingDecorationIdSet.has(decorationId)
  );

  return {
    rewards: {
      stamps,
      unlockedDecorationIds: [
        ...existingDecorationIds,
        ...newlyUnlockedDecorationIds
      ]
    },
    stampsAwarded,
    newlyUnlockedDecorationIds
  };
}

function assertNonNegativeInteger(value: number, label: string): void {
  if (!Number.isSafeInteger(value) || value < 0) {
    throw new RangeError(label + " must be a non-negative safe integer");
  }
}
